import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	invalidateGetOAuthClients,
	IOAuthClient,
	useDeleteOAuthClientMutation,
	useGetOAuthClients,
} from '../../../../queries';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ClipboardIcon2 } from '../../../components/svgs/icons/ClipboardIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { SimpleTooltip } from '../../../components/Tooltip';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { CreateOAuthClientDialog } from './CreateOAuthClientDialog';
import { styleSheet } from './styles';

export const OAuthClients = () => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('OAuthClients');
	const errorMessages = useErrorMessages();
	const [showCreateDialog, setShowCreateDialog] = React.useState(false);
	const [clientToDelete, setClientToDelete] = React.useState<IOAuthClient | null>(null);

	const oauthClientsQuery = useGetOAuthClients({
		enabled: userSession.isAdmin && Boolean(userSession.account.features.publicApi?.enabled),
		refetchOnWindowFocus: false,
	});

	const deleteOAuthClient = useDeleteOAuthClientMutation({
		onSuccess: () => {
			toast.success('OAuth client has been deleted');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('DeleteOAuthClient-Error', error);
		},
		// Invalidate on settle (success or error) so a flaky-network delete that succeeded
		// server-side still reconciles the list, since this query doesn't refetch on focus.
		onSettled: () => {
			invalidateGetOAuthClients();
		},
	});

	const copyClientId = (clientId: string) => {
		navigator.clipboard
			.writeText(clientId)
			.then(() => toast.success('Client ID has been copied to your clipboard'))
			.catch(() => toast.error('Failed to copy Client ID to clipboard'));
	};

	const onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		const client = clientToDelete;
		setClientToDelete(null);
		if (client && result?.isDestructive && !cancel) {
			deleteOAuthClient.mutate(client.id);
		}
	};

	if (oauthClientsQuery.isLoading) {
		return <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />;
	}

	const clients = oauthClientsQuery.data ?? [];

	const apiDocsUrl = `${import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? ''}/docs`;

	return (
		<section className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<section className={css(styleSheet.headerCopy)}>
					<h1 className={css(baseStyleSheet.breadcrumbTitle)}>OAuth Clients</h1>
					<small>
						Register and manage OAuth clients that let external applications connect to the Levitate public API on
						behalf of your users. Unlike a personal API key, which is a single static credential scoped to one user, an
						OAuth client authenticates each user individually through OAuth and grants only the permissions they
						approve, making it the right fit for apps used by many people.
					</small>
				</section>
				<section className={css(styleSheet.headerActions)}>
					<Button variant='primary' onClick={() => setShowCreateDialog(true)}>
						Create OAuth Client
					</Button>
				</section>
			</header>
			<div className={css(styleSheet.card)}>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn>Name</DataGridColumn>
						<DataGridColumn>Client ID</DataGridColumn>
						<DataGridColumn>Redirect URIs</DataGridColumn>
						<DataGridColumn>Scopes</DataGridColumn>
						<DataGridColumn>Created</DataGridColumn>
						<DataGridColumn>Actions</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{clients.map((client: IOAuthClient) => (
							<DataGridRow key={`oauth-client-${client.id}`}>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									<div className={css(styleSheet.nameContent)}>
										{client.name}
										{client.description ? (
											<small className={css(styleSheet.description)}>{client.description}</small>
										) : null}
									</div>
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									<span className={css(styleSheet.clientIdCell)}>
										<span className={css(styleSheet.clientIdValue)}>{client.clientId}</span>
										<SimpleTooltip title='Copy to clipboard'>
											<Button
												variant='link'
												size='icon'
												onClick={() => copyClientId(client.clientId)}
												aria-label='Copy to clipboard'
											>
												<ClipboardIcon2 />
											</Button>
										</SimpleTooltip>
									</span>
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									{client.redirectUris?.length ? (
										<div className={css(styleSheet.redirectUris)}>
											{client.redirectUris.map((uri, index) => (
												// eslint-disable-next-line react/no-array-index-key
												<span key={`${uri}-${index}`} className={css(styleSheet.redirectUri)}>
													{uri}
												</span>
											))}
										</div>
									) : (
										'—'
									)}
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									{client.scopes?.length ? (
										<div className={css(styleSheet.scopes)}>
											{client.scopes.map(scope => (
												<span key={scope}>{scope}</span>
											))}
										</div>
									) : (
										'—'
									)}
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									{client.creationDate ? moment(client.creationDate).format('MM/DD/YYYY') : '—'}
									{client.creator ? (
										<small className={css(styleSheet.createdBy)}>
											by {getDisplayName(client.creator as Api.IPrincipal)}
										</small>
									) : null}
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.nameCell]}>
									<Dropdown>
										<DropdownEllipsisTrigger />
										<DropdownContent>
											<DropdownItem onClick={() => setClientToDelete(client)}>Delete</DropdownItem>
										</DropdownContent>
									</Dropdown>
								</DataGridRowItem>
							</DataGridRow>
						))}
						{clients.length < 1 ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>No OAuth clients yet</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
			</div>

			<CreateOAuthClientDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />

			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!clientToDelete,
					onRequestClose: onDeleteConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title={
					<span>
						{`Are you sure you want to delete the "${clientToDelete?.name || ''}" OAuth client?`}
						<br />
						<b>Apps using it will no longer be able to authenticate.</b>
					</span>
				}
			/>

			<footer className={css(styleSheet.docsFooter)}>
				<span>Need help?</span>
				<Button variant='link' asChild>
					<a href={apiDocsUrl} target='_blank' rel='noopener noreferrer'>
						View the API documentation
					</a>
				</Button>
			</footer>
		</section>
	);
};
